// Reportes contables: Libro Mayor, Balance de Comprobación, Estado de Situación, Estado de Resultados.
import { Fragment, useState } from 'react';
import { AvisoRenombre } from '@/components/ui/AvisoRenombre';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Download, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { DimOpt, centrosList, exportarCSV, proyectosList, field, usd, hoy, inicioMes, rpc } from './shared';
import { Imprimible, PrintButton } from '@/components/print/DocPrint';
import { BuscadorCuenta } from '@/components/ui/BuscadorCuenta';
import { CompararSelect, TablaComparativa, csvComparativo, rotulo, useComparado } from './Comparativo';

const CsvBtn = ({ onClick }: { onClick: () => void }) => (
  <button onClick={onClick} className="no-print inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-sm hover:bg-muted"><Download className="w-4 h-4" /> CSV</button>
);

function ProyectoSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation('contabilidad');
  const { data: proyectos = [] } = useQuery<DimOpt[]>({ queryKey: ['cont-proyectos'], queryFn: proyectosList });
  return (
    <label className="flex flex-col text-xs text-muted-foreground">{t('comun.proyecto')}
      <select className={`${field} w-52 mt-1`} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{t('comun.todos')}</option>
        {proyectos.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
      </select>
    </label>
  );
}
function CentroSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation('contabilidad');
  const { data: centros = [] } = useQuery<DimOpt[]>({ queryKey: ['cont-centros'], queryFn: centrosList });
  return (
    <label className="flex flex-col text-xs text-muted-foreground">{t('comun.centro_costo')}
      <select className={`${field} w-52 mt-1`} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{t('comun.todos')}</option>
        {centros.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
      </select>
    </label>
  );
}
const MODULOS: { v: string; label: string }[] = [
  { v: 'manual', label: 'Manual' }, { v: 'financiero', label: 'Financiero' }, { v: 'nomina', label: 'Nómina' },
  { v: 'pos', label: 'POS' }, { v: 'control_bancario', label: 'Control Bancario' }, { v: 'inventario', label: 'Inventario' },
  { v: 'distribucion', label: 'Prorrateo / Distribución' }, { v: 'cierre', label: 'Cierre' },
  { v: 'apertura', label: 'Apertura' }, { v: 'otro', label: 'Otro' },
];
function ModuloSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation('contabilidad');
  return (
    <label className="flex flex-col text-xs text-muted-foreground">{t('comun.modulo_origen')}
      <select className={`${field} w-48 mt-1`} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{t('comun.todos')}</option>
        {MODULOS.map((m) => <option key={m.v} value={m.v}>{t(`modulos.${m.v}`, { defaultValue: m.label })}</option>)}
      </select>
    </label>
  );
}
const Fecha = ({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) => (
  <label className="flex flex-col text-xs text-muted-foreground">{label}
    <input type="date" className={`${field} mt-1`} value={value} onChange={(e) => onChange(e.target.value)} /></label>
);

// ---- Libro Mayor ----------------------------------------------------------
export function LibroMayor() {
  const { t } = useTranslation('contabilidad');
  const [cuenta, setCuenta] = useState('');
  const [desde, setDesde] = useState(inicioMes);
  const [hasta, setHasta] = useState(hoy);
  const [proy, setProy] = useState('');
  const [centro, setCentro] = useState('');
  const [modulo, setModulo] = useState('');
  const { data, isFetching } = useQuery({
    queryKey: ['cont-mayor', cuenta, desde, hasta, proy, centro, modulo], enabled: !!cuenta,
    queryFn: () => rpc<any>('cont_libro_mayor', { _cuenta_id: cuenta, _desde: desde, _hasta: hasta, _proyecto: proy || null, _centro: centro || null, _modulo: modulo || null }),
  });
  let saldo = Number(data?.saldo_inicial || 0);
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-2"><h1 className="text-xl font-semibold">{t('libro_mayor.titulo')}</h1>{cuenta && <div className="flex gap-2">
        <CsvBtn onClick={() => exportarCSV('libro-mayor', [t('comun.fecha'), t('comun.num'), t('comun.glosa'), t('comun.debe'), t('comun.haber')], (data?.movimientos || []).map((m: any) => [m.fecha, m.numero, m.glosa, m.debe, m.haber]))} /><PrintButton /></div>}</div>
      <Card><CardContent className="flex flex-wrap items-end gap-3 pt-4">
        <label className="flex flex-col text-xs text-muted-foreground w-80">{t('comun.cuenta')}
          <BuscadorCuenta value={cuenta} onChange={setCuenta} placeholder={t('comun.guion_selecciona')} /></label>
        <Fecha label={t('comun.desde')} value={desde} onChange={setDesde} /><Fecha label={t('comun.hasta')} value={hasta} onChange={setHasta} /><ProyectoSelect value={proy} onChange={setProy} /><CentroSelect value={centro} onChange={setCentro} /><ModuloSelect value={modulo} onChange={setModulo} />
      </CardContent></Card>
      {cuenta && (
        <Imprimible titulo={t('libro_mayor.titulo')} subtitulo={t('comun.del_al', { desde, hasta })}>
        <Card><CardHeader className="pb-2"><CardTitle className="text-base">{t('libro_mayor.movimientos')}</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">{t('comun.fecha')}</th><th className="py-2 pr-3">{t('comun.num')}</th><th className="py-2 pr-3">{t('comun.glosa')}</th><th className="py-2 pr-3 text-right">{t('comun.debe')}</th><th className="py-2 pr-3 text-right">{t('comun.haber')}</th><th className="py-2 pr-3 text-right">{t('comun.saldo')}</th></tr></thead>
              <tbody>
                <tr className="border-b text-muted-foreground"><td className="py-2 pr-3" colSpan={5}>{t('libro_mayor.saldo_inicial')}</td><td className="py-2 pr-3 text-right">{usd(saldo)}</td></tr>
                {(data?.movimientos || []).map((m: any, i: number) => { saldo += Number(m.debe) - Number(m.haber); return (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-1.5 pr-3 whitespace-nowrap">{m.fecha}</td><td className="py-1.5 pr-3 font-mono text-xs">{m.numero}</td>
                    <td className="py-1.5 pr-3">{m.glosa}</td><td className="py-1.5 pr-3 text-right">{Number(m.debe) ? usd(m.debe) : ''}</td>
                    <td className="py-1.5 pr-3 text-right">{Number(m.haber) ? usd(m.haber) : ''}</td><td className="py-1.5 pr-3 text-right">{usd(saldo)}</td>
                  </tr>); })}
                <tr className="border-t font-medium"><td className="py-2 pr-3" colSpan={3}>{t('comun.totales')}</td><td className="py-2 pr-3 text-right">{usd(data?.total_debe)}</td><td className="py-2 pr-3 text-right">{usd(data?.total_haber)}</td><td className="py-2 pr-3 text-right">{usd(saldo)}</td></tr>
              </tbody>
            </table>
          </CardContent></Card>
        </Imprimible>
      )}
    </div>
  );
}

// ---- Balance de Comprobación ----------------------------------------------
export function Comprobacion() {
  const { t: tr } = useTranslation('contabilidad');
  const [desde, setDesde] = useState(inicioMes); const [hasta, setHasta] = useState(hoy); const [proy, setProy] = useState(''); const [centro, setCentro] = useState('');
  const { data = [], isFetching } = useQuery<any[]>({ queryKey: ['cont-comp', desde, hasta, proy, centro], queryFn: () => rpc<any[]>('cont_balance_comprobacion', { _desde: desde, _hasta: hasta, _proyecto: proy || null, _centro: centro || null }) });
  const t = data.reduce((a, r) => ({ debe: a.debe + Number(r.debe), haber: a.haber + Number(r.haber), sd: a.sd + Number(r.saldo_deudor), sa: a.sa + Number(r.saldo_acreedor) }), { debe: 0, haber: 0, sd: 0, sa: 0 });
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-2"><h1 className="text-xl font-semibold">{tr('comprobacion.titulo')}</h1><div className="flex gap-2">
        <CsvBtn onClick={() => exportarCSV('balance-comprobacion', [tr('comun.codigo'), tr('comun.cuenta'), tr('comun.debe'), tr('comun.haber'), tr('comprobacion.saldo_deudor'), tr('comprobacion.saldo_acreedor')], data.map((r) => [r.codigo, r.nombre, r.debe, r.haber, r.saldo_deudor, r.saldo_acreedor]))} /><PrintButton /></div></div>
      <Card><CardContent className="flex flex-wrap items-end gap-3 pt-4"><Fecha label={tr('comun.desde')} value={desde} onChange={setDesde} /><Fecha label={tr('comun.hasta')} value={hasta} onChange={setHasta} /><ProyectoSelect value={proy} onChange={setProy} /><CentroSelect value={centro} onChange={setCentro} /></CardContent></Card>
      <Imprimible titulo={tr('comprobacion.titulo')} subtitulo={tr('comun.del_al', { desde, hasta })}>
      <Card><CardContent className="overflow-x-auto pt-4">
        {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">{tr('comun.codigo')}</th><th className="py-2 pr-3">{tr('comun.cuenta')}</th><th className="py-2 pr-3 text-right">{tr('comun.debe')}</th><th className="py-2 pr-3 text-right">{tr('comun.haber')}</th><th className="py-2 pr-3 text-right">{tr('comprobacion.saldo_deudor')}</th><th className="py-2 pr-3 text-right">{tr('comprobacion.saldo_acreedor')}</th></tr></thead>
          <tbody>
            {data.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">{tr('comun.sin_movimientos')}</td></tr>}
            {data.map((r, i) => (
              <tr key={i} className="border-b last:border-0"><td className="py-1.5 pr-3 font-mono text-xs">{r.codigo}</td><td className="py-1.5 pr-3">{r.nombre}</td>
                <td className="py-1.5 pr-3 text-right">{usd(r.debe)}</td><td className="py-1.5 pr-3 text-right">{usd(r.haber)}</td>
                <td className="py-1.5 pr-3 text-right">{Number(r.saldo_deudor) ? usd(r.saldo_deudor) : ''}</td><td className="py-1.5 pr-3 text-right">{Number(r.saldo_acreedor) ? usd(r.saldo_acreedor) : ''}</td></tr>
            ))}
            {data.length > 0 && <tr className="border-t font-medium"><td colSpan={2} className="py-2 pr-3">{tr('comun.totales')}</td><td className="py-2 pr-3 text-right">{usd(t.debe)}</td><td className="py-2 pr-3 text-right">{usd(t.haber)}</td><td className="py-2 pr-3 text-right">{usd(t.sd)}</td><td className="py-2 pr-3 text-right">{usd(t.sa)}</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
      </Imprimible>
    </div>
  );
}

const Seccion =({ titulo, items, total }: { titulo: string; items: any[]; total: number }) => (
  <>
    <tr className="bg-muted/50 font-semibold"><td className="py-1.5 px-3" colSpan={2}>{titulo}</td><td className="py-1.5 px-3 text-right">{usd(total)}</td></tr>
    {(items || []).map((r, i) => <tr key={i} className="border-b last:border-0"><td className="py-1 px-3 font-mono text-xs w-24">{r.codigo}</td><td className="py-1 px-3">{r.nombre}</td><td className="py-1 px-3 text-right">{usd(r.valor)}</td></tr>)}
  </>
);

// ---- Estado de Situación (Balance) ----------------------------------------
export function EstadoSituacion() {
  const { t } = useTranslation('contabilidad');
  const [hasta, setHasta] = useState(hoy); const [proy, setProy] = useState(''); const [centro, setCentro] = useState('');
  const [cmp, setCmp] = useState('');
  const { data, isFetching } = useQuery<any>({ queryKey: ['cont-bal', hasta, proy, centro], queryFn: () => rpc<any>('cont_estado_situacion', { _hasta: hasta, _proyecto: proy || null, _centro: centro || null }) });
  const comp = useComparado('situacion', cmp, { hasta, proyecto: proy, centro });
  const totPasPat = Number(data?.total_pasivo || 0) + Number(data?.total_patrimonio || 0) + Number(data?.resultado_ejercicio || 0);
  const exportar = () => {
    if (cmp && comp.data) { const c = csvComparativo(comp.data); return exportarCSV(`estado-situacion-comparado-${hasta}`, c.headers, c.filas); }
    exportarCSV('estado-situacion', [t('situacion.csv_seccion'), t('comun.codigo'), t('comun.cuenta'), t('comun.valor')], [
      ...(data?.activos || []).map((r: any) => [t('situacion.activo'), r.codigo, r.nombre, r.valor]),
      ...(data?.pasivos || []).map((r: any) => [t('situacion.pasivo'), r.codigo, r.nombre, r.valor]),
      ...(data?.patrimonio || []).map((r: any) => [t('situacion.patrimonio'), r.codigo, r.nombre, r.valor])]);
  };
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-2"><h1 className="text-xl font-semibold">{t('situacion.titulo')}</h1><div className="flex gap-2">
        <CsvBtn onClick={exportar} /><PrintButton /></div></div>
      <Card><CardContent className="flex flex-wrap items-end gap-3 pt-4"><Fecha label={t('comun.al_label')} value={hasta} onChange={setHasta} /><ProyectoSelect value={proy} onChange={setProy} /><CentroSelect value={centro} onChange={setCentro} /><CompararSelect value={cmp} onChange={setCmp} /></CardContent></Card>
      <Imprimible titulo={t('situacion.titulo')}
        subtitulo={cmp && comp.data ? t('comun.comparado_con', { base: t('comun.al', { hasta }), otro: rotulo(comp.data.anterior) }) : t('comun.al', { hasta })}>
      <Card><CardContent className="overflow-x-auto pt-4">
        {cmp ? <TablaComparativa data={comp.data} cargando={comp.isFetching} /> : <>
        {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
        <table className="w-full text-sm">
          <tbody>
            <Seccion titulo={t('situacion.activo')} items={data?.activos} total={Number(data?.total_activo || 0)} />
            <Seccion titulo={t('situacion.pasivo')} items={data?.pasivos} total={Number(data?.total_pasivo || 0)} />
            <Seccion titulo={t('situacion.patrimonio')} items={data?.patrimonio} total={Number(data?.total_patrimonio || 0)} />
            <tr className="border-b"><td className="py-1 px-3" colSpan={2}>{t('situacion.resultado_ejercicio')}</td><td className="py-1 px-3 text-right">{usd(data?.resultado_ejercicio)}</td></tr>
            <tr className="border-t-2 font-bold"><td className="py-2 px-3" colSpan={2}>{t('situacion.total_activo')}</td><td className="py-2 px-3 text-right">{usd(data?.total_activo)}</td></tr>
            <tr className="font-bold"><td className="py-2 px-3" colSpan={2}>{t('situacion.total_pasivo_patrimonio')}</td><td className="py-2 px-3 text-right">{usd(totPasPat)}</td></tr>
          </tbody>
        </table>
        <p className={`mt-2 text-sm ${Math.abs(Number(data?.total_activo || 0) - totPasPat) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
          {Math.abs(Number(data?.total_activo || 0) - totPasPat) < 0.01 ? t('situacion.cuadrado') : t('situacion.descuadre', { valor: usd(Number(data?.total_activo || 0) - totPasPat) })}
        </p></>}
      </CardContent></Card>
      </Imprimible>
    </div>
  );
}

// ---- Bloques de la presentación NIIF 18 ------------------------------------
function CatBlock({ titulo, cat }: { titulo: string; cat: any }) {
  const lineas = cat?.lineas || [];
  if (!lineas.length && !Number(cat?.total)) return null;
  return (
    <>
      <tr className="bg-muted/50 font-semibold"><td className="py-1.5 px-3" colSpan={3}>{titulo}</td></tr>
      {lineas.map((r: any, i: number) => (
        <tr key={i} className="border-b last:border-0"><td className="py-1 px-3 font-mono text-xs w-24">{r.codigo}</td><td className="py-1 px-3">{r.nombre}</td><td className="py-1 px-3 text-right">{usd(r.valor)}</td></tr>
      ))}
    </>
  );
}
const SubtotalRow = ({ label, value, strong }: { label: string; value: number; strong?: boolean }) => (
  <tr className={strong ? 'border-y-2 border-primary/60 font-bold bg-primary/5' : 'border-t font-semibold'}>
    <td className="py-2 px-3" colSpan={2}>{label}</td><td className="py-2 px-3 text-right">{usd(value)}</td></tr>
);
// ---- Estado de Resultados: UNA pantalla, DOS presentaciones ----------------
//
// NIIF 18 no es otro estado: es otra forma de PRESENTAR el mismo resultado. La
// norma reordena y exige subtotales, pero no cambia la medición — el resultado
// del período sale idéntico por las dos vías. Tenerlas en dos opciones de menú
// obligaba a elegir antes de mirar, y a volver atrás para comparar.
//
// Al cambiar de presentación no se vuelve a pedir nada: los filtros son los
// mismos y React Query conserva lo ya traído, así que el ida y vuelta entre
// ambas es inmediato.
type Presentacion = 'tradicional' | 'niif18';

const PRESENTACIONES: { v: Presentacion }[] = [{ v: 'tradicional' }, { v: 'niif18' }];

function SelectorPresentacion({ value, onChange }: { value: Presentacion; onChange: (v: Presentacion) => void }) {
  const { t } = useTranslation('contabilidad');
  return (
    <div className="flex flex-col text-xs text-muted-foreground no-print">
      {t('resultados.presentacion')}
      <div className="mt-1 inline-flex rounded-md border p-0.5" role="group" aria-label={t('resultados.pres_aria')}>
        {PRESENTACIONES.map((p) => (
          <button key={p.v} type="button" onClick={() => onChange(p.v)}
            aria-pressed={value === p.v} title={t(`resultados.pres_${p.v}_ayuda`)}
            className={`rounded px-3 py-1.5 text-sm transition-colors ${
              value === p.v ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'}`}>
            {t(`resultados.pres_${p.v}`)}
          </button>
        ))}
      </div>
    </div>
  );
}

export function EstadoResultados({ presentacionInicial = 'tradicional' }: { presentacionInicial?: Presentacion } = {}) {
  const { t } = useTranslation('contabilidad');
  const catL = (k: string) => t(`resultados.cat_${k}`);
  const [pres, setPres] = useState<Presentacion>(presentacionInicial);
  const [desde, setDesde] = useState(inicioMes); const [hasta, setHasta] = useState(hoy);
  const [proy, setProy] = useState(''); const [centro, setCentro] = useState('');
  const [cmp, setCmp] = useState('');
  const esNiif = pres === 'niif18';

  // Sólo se consulta la presentación que se está mirando; la otra queda en la
  // cache de React Query en cuanto se visita una vez.
  const trad = useQuery<any>({
    enabled: !esNiif,
    queryKey: ['cont-pyg', desde, hasta, proy, centro],
    queryFn: () => rpc<any>('cont_estado_resultados', { _desde: desde, _hasta: hasta, _proyecto: proy || null, _centro: centro || null }),
  });
  const niif = useQuery<any>({
    enabled: esNiif,
    queryKey: ['cont-pyg-niif18', desde, hasta, proy, centro],
    queryFn: () => rpc<any>('cont_estado_resultados_niif18', { _desde: desde, _hasta: hasta, _proyecto: proy || null, _centro: centro || null }),
  });
  const comp = useComparado(esNiif ? 'niif18' : 'resultados', cmp, { desde, hasta, proyecto: proy, centro });

  const data = esNiif ? niif.data : trad.data;
  const isFetching = esNiif ? niif.isFetching : trad.isFetching;
  const c = niif.data?.categorias || {}; const st = niif.data?.subtotales || {};
  const sinClasif = Number(niif.data?.sin_clasificar || 0);

  const titulo = esNiif ? t('resultados.titulo_niif18') : t('resultados.titulo');
  const exportar = () => {
    if (cmp && comp.data) {
      const x = csvComparativo(comp.data);
      return exportarCSV(`estado-resultados${esNiif ? '-niif18' : ''}-comparado-${desde}_${hasta}`, x.headers, x.filas);
    }
    if (esNiif) {
      return exportarCSV(`estado-resultados-niif18-${desde}_${hasta}`, [t('resultados.csv_categoria'), t('comun.codigo'), t('comun.cuenta'), t('comun.valor')],
        ['operacion', 'inversion', 'financiacion', 'impuestos', 'discontinuadas']
          .flatMap((k) => (c[k]?.lineas || []).map((r: any) => [catL(k), r.codigo, r.nombre, r.valor])));
    }
    exportarCSV('estado-resultados', [t('resultados.csv_seccion'), t('comun.codigo'), t('comun.cuenta'), t('comun.valor')], [
      ...(data?.ingresos || []).map((r: any) => [t('resultados.ingresos'), r.codigo, r.nombre, r.valor]),
      ...(data?.costos || []).map((r: any) => [t('resultados.costos'), r.codigo, r.nombre, r.valor]),
      ...(data?.gastos || []).map((r: any) => [t('resultados.gastos'), r.codigo, r.nombre, r.valor])]);
  };

  return (
    <div className="p-6 space-y-4">
      <AvisoRenombre clave="cont-pyg-niif18" antes="Estado de Resultados (NIIF 18)"
        texto={<Trans i18nKey="resultados.aviso" ns="contabilidad" components={{ 1: <b />, 3: <b /> }} />} />
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">{titulo}</h1>
        <div className="flex gap-2"><CsvBtn onClick={exportar} /><PrintButton /></div>
      </div>
      {esNiif && (
        <p className="text-sm text-muted-foreground no-print">
          {t('resultados.nota_niif18')}
        </p>
      )}

      <Card><CardContent className="flex flex-wrap items-end gap-3 pt-4">
        <SelectorPresentacion value={pres} onChange={setPres} />
        <Fecha label={t('comun.desde')} value={desde} onChange={setDesde} />
        <Fecha label={t('comun.hasta')} value={hasta} onChange={setHasta} />
        <ProyectoSelect value={proy} onChange={setProy} />
        <CentroSelect value={centro} onChange={setCentro} />
        <CompararSelect value={cmp} onChange={setCmp} />
      </CardContent></Card>

      {esNiif && sinClasif !== 0 && (
        <p className="no-print text-sm text-amber-600">
          {t('resultados.sin_clasif', { valor: usd(sinClasif) })}
        </p>
      )}

      <Imprimible titulo={titulo}
        subtitulo={cmp && comp.data ? t('comun.comparado_con', { base: t('comun.del_al', { desde, hasta }), otro: rotulo(comp.data.anterior) }) : t('comun.del_al', { desde, hasta })}>
        <Card><CardContent className="overflow-x-auto pt-4">
          {cmp ? <TablaComparativa data={comp.data} cargando={comp.isFetching} /> : <>
            {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
            {esNiif ? (
              <table className="w-full text-sm"><tbody>
                <CatBlock titulo={catL('operacion')} cat={c.operacion} />
                {Number(st.utilidad_bruta) !== Number(st.utilidad_operativa) && <SubtotalRow label={t('resultados.utilidad_bruta')} value={Number(st.utilidad_bruta || 0)} />}
                <SubtotalRow label={t('resultados.utilidad_operativa')} value={Number(st.utilidad_operativa || 0)} strong />
                <CatBlock titulo={catL('inversion')} cat={c.inversion} />
                <SubtotalRow label={t('resultados.antes_fin_imp')} value={Number(st.antes_fin_impuestos || 0)} strong />
                <CatBlock titulo={catL('financiacion')} cat={c.financiacion} />
                <SubtotalRow label={t('resultados.antes_imp')} value={Number(st.antes_impuestos || 0)} />
                <CatBlock titulo={catL('impuestos')} cat={c.impuestos} />
                <CatBlock titulo={catL('discontinuadas')} cat={c.discontinuadas} />
                <SubtotalRow label={t('resultados.resultado_periodo')} value={Number(st.resultado_periodo || 0)} strong />
              </tbody></table>
            ) : (
              <table className="w-full text-sm"><tbody>
                <Seccion titulo={t('resultados.ingresos')} items={data?.ingresos} total={Number(data?.total_ingresos || 0)} />
                <Seccion titulo={t('resultados.costos')} items={data?.costos} total={Number(data?.total_costos || 0)} />
                <Seccion titulo={t('resultados.gastos')} items={data?.gastos} total={Number(data?.total_gastos || 0)} />
                <tr className="border-t-2 font-bold"><td className="py-2 px-3" colSpan={2}>{t('resultados.utilidad_perdida')}</td>
                  <td className={`py-2 px-3 text-right ${Number(data?.utilidad || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{usd(data?.utilidad)}</td></tr>
              </tbody></table>
            )}
          </>}
        </CardContent></Card>
      </Imprimible>
    </div>
  );
}

// La ruta /contabilidad/resultados-niif18 sigue resolviendo y abre la pantalla
// fusionada con esa presentación puesta: nadie pierde un enlace ni una pestaña
// guardada. Mismo criterio que la fusión de cheques posfechados (script 759).
export const EstadoResultadosNIIF18 = () => <EstadoResultados presentacionInicial="niif18" />;

// ---- Gastos por Naturaleza (nota NIIF 18) ----------------------------------
export function GastosPorNaturaleza() {
  const [desde, setDesde] = useState(inicioMes); const [hasta, setHasta] = useState(hoy);
  const [proy, setProy] = useState(''); const [centro, setCentro] = useState('');
  const { data, isFetching } = useQuery<any>({ queryKey: ['cont-gxn', desde, hasta, proy, centro], queryFn: () => rpc<any>('cont_gastos_naturaleza', { _desde: desde, _hasta: hasta, _proyecto: proy || null, _centro: centro || null }) });
  const matriz = data?.matriz || []; const t = data?.totales || {};
  const cols: [string, string][] = [['depreciacion', 'Depreciación'], ['amortizacion', 'Amortización'], ['beneficios_empleados', 'Beneficios a empleados'], ['deterioro', 'Deterioro'], ['inventarios', 'Inventarios'], ['otros', 'Otros'], ['total', 'Total']];
  const exportar = () => exportarCSV(`gastos-naturaleza-${desde}_${hasta}`, ['Función', ...cols.map((c) => c[1])], matriz.map((r: any) => [r.funcion, ...cols.map((c) => r[c[0]])]));
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-2"><h1 className="text-xl font-semibold">Gastos por Naturaleza (NIIF 18)</h1><div className="flex gap-2"><CsvBtn onClick={exportar} /><PrintButton /></div></div>
      <p className="text-sm text-muted-foreground no-print">Nota exigida por NIIF 18 al presentar los gastos operativos por función: importes de cada naturaleza por línea funcional. La naturaleza se define por cuenta en el Plan de Cuentas.</p>
      <Card><CardContent className="flex flex-wrap items-end gap-3 pt-4"><Fecha label="Desde" value={desde} onChange={setDesde} /><Fecha label="Hasta" value={hasta} onChange={setHasta} /><ProyectoSelect value={proy} onChange={setProy} /><CentroSelect value={centro} onChange={setCentro} /></CardContent></Card>
      <Imprimible titulo="Gastos por Naturaleza (NIIF 18)" subtitulo={`Del ${desde} al ${hasta}`}>
      <Card><CardContent className="overflow-x-auto pt-4">
        {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Función</th>{cols.map((c) => <th key={c[0]} className="py-2 pr-3 text-right">{c[1]}</th>)}</tr></thead>
          <tbody>
            {matriz.length === 0 && !isFetching && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin gastos operativos en el período</td></tr>}
            {matriz.map((r: any, i: number) => (
              <tr key={i} className="border-b last:border-0"><td className="py-1.5 pr-3">{r.funcion}</td>
                {cols.map((c) => <td key={c[0]} className={`py-1.5 pr-3 text-right ${c[0] === 'total' ? 'font-medium' : ''}`}>{Number(r[c[0]]) ? usd(r[c[0]]) : '—'}</td>)}
              </tr>
            ))}
            {matriz.length > 0 && <tr className="border-t-2 font-bold"><td className="py-2 pr-3">Total</td>{cols.map((c) => <td key={c[0]} className="py-2 pr-3 text-right">{usd(t[c[0]])}</td>)}</tr>}
          </tbody>
        </table>
      </CardContent></Card>
      </Imprimible>
    </div>
  );
}

// ---- Libro Diario (formal) -------------------------------------------------
export function LibroDiario() {
  const { t } = useTranslation('contabilidad');
  const [desde, setDesde] = useState(inicioMes); const [hasta, setHasta] = useState(hoy);
  const [proy, setProy] = useState(''); const [centro, setCentro] = useState(''); const [modulo, setModulo] = useState('');
  const { data, isFetching } = useQuery<any>({ queryKey: ['cont-diario', desde, hasta, proy, centro, modulo], queryFn: () => rpc<any>('cont_libro_diario', { _desde: desde, _hasta: hasta, _proyecto: proy || null, _centro: centro || null, _modulo: modulo || null }) });
  const asientos = data?.asientos || [];
  const exportar = () => exportarCSV(`libro-diario-${desde}_${hasta}`, [t('comun.fecha'), t('comun.num'), t('comun.cuenta'), t('comun.glosa'), t('comun.debe'), t('comun.haber')],
    asientos.flatMap((a: any) => (a.lineas || []).map((l: any) => [a.fecha, a.numero, `${l.codigo} ${l.nombre}`, l.glosa || a.glosa, l.debe, l.haber])));
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-2"><h1 className="text-xl font-semibold">{t('libro_diario.titulo')}</h1><div className="flex gap-2"><CsvBtn onClick={exportar} /><PrintButton /></div></div>
      <Card><CardContent className="flex flex-wrap items-end gap-3 pt-4"><Fecha label={t('comun.desde')} value={desde} onChange={setDesde} /><Fecha label={t('comun.hasta')} value={hasta} onChange={setHasta} /><ProyectoSelect value={proy} onChange={setProy} /><CentroSelect value={centro} onChange={setCentro} /><ModuloSelect value={modulo} onChange={setModulo} /></CardContent></Card>
      <Imprimible titulo={t('libro_diario.titulo')} subtitulo={t('comun.del_al', { desde, hasta })}>
        <Card><CardContent className="overflow-x-auto pt-4">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">{t('libro_diario.col_fecha_num')}</th><th className="py-2 pr-3">{t('comun.cuenta')}</th><th className="py-2 pr-3">{t('comun.glosa')}</th><th className="py-2 pr-3 text-right">{t('comun.debe')}</th><th className="py-2 pr-3 text-right">{t('comun.haber')}</th></tr></thead>
            <tbody>
              {asientos.length === 0 && !isFetching && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">{t('comun.sin_asientos')}</td></tr>}
              {asientos.map((a: any) => (
                <Fragment key={a.numero}>
                  <tr className="bg-muted/40 border-b"><td className="py-1.5 pr-3 font-mono text-xs whitespace-nowrap">{a.fecha} · #{a.numero}</td><td className="py-1.5 pr-3 font-medium" colSpan={2}>{a.glosa || '—'}{a.modulo && <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">{a.modulo}</span>}</td><td colSpan={2}></td></tr>
                  {(a.lineas || []).map((l: any, i: number) => (
                    <tr key={a.numero + '-' + i} className="border-b last:border-0">
                      <td></td><td className="py-1 pr-3"><span className="font-mono text-xs">{l.codigo}</span> {l.nombre}</td><td className="py-1 pr-3 text-muted-foreground">{l.glosa || ''}</td>
                      <td className="py-1 pr-3 text-right">{Number(l.debe) ? usd(l.debe) : ''}</td><td className="py-1 pr-3 text-right">{Number(l.haber) ? usd(l.haber) : ''}</td>
                    </tr>
                  ))}
                </Fragment>
              ))}
              {asientos.length > 0 && <tr className="border-t-2 font-bold"><td colSpan={3} className="py-2 pr-3 text-right">{t('comun.totales')}</td><td className="py-2 pr-3 text-right">{usd(data?.total_debe)}</td><td className="py-2 pr-3 text-right">{usd(data?.total_haber)}</td></tr>}
            </tbody>
          </table>
        </CardContent></Card>
      </Imprimible>
    </div>
  );
}

// ---- Estado de Flujo de Efectivo (método directo) --------------------------
export function FlujoEfectivo() {
  const { t } = useTranslation('contabilidad');
  const [desde, setDesde] = useState(inicioMes); const [hasta, setHasta] = useState(hoy);
  const [cmp, setCmp] = useState('');
  const { data, isFetching } = useQuery<any>({ queryKey: ['cont-efe', desde, hasta], queryFn: () => rpc<any>('cont_flujo_efectivo', { _desde: desde, _hasta: hasta }) });
  const comp = useComparado('flujo', cmp, { desde, hasta });
  const exportar = () => {
    if (cmp && comp.data) { const c = csvComparativo(comp.data); return exportarCSV(`flujo-efectivo-comparado-${desde}_${hasta}`, c.headers, c.filas); }
    exportarCSV(`flujo-efectivo-${desde}_${hasta}`, [t('flujo.csv_tipo'), t('comun.codigo'), t('comun.cuenta'), t('comun.valor')], [
      ...(data?.entradas || []).map((r: any) => [t('flujo.csv_entrada'), r.codigo, r.nombre, r.valor]),
      ...(data?.salidas || []).map((r: any) => [t('flujo.csv_salida'), r.codigo, r.nombre, r.valor])]);
  };
  return (
    <div className="p-6 space-y-4">
      <AvisoRenombre clave="cont-flujo" antes="Flujo de Efectivo" texto={<>{t('flujo.renombre_pre')}<b>«Flujo de Efectivo»</b>.</>} />
      <div className="flex items-center justify-between gap-2"><h1 className="text-xl font-semibold">{t('flujo.titulo')}</h1><div className="flex gap-2"><CsvBtn onClick={exportar} /><PrintButton /></div></div>
      <p className="text-sm text-muted-foreground no-print">{t('flujo.nota')}</p>
      <Card><CardContent className="flex flex-wrap items-end gap-3 pt-4"><Fecha label={t('comun.desde')} value={desde} onChange={setDesde} /><Fecha label={t('comun.hasta')} value={hasta} onChange={setHasta} /><CompararSelect value={cmp} onChange={setCmp} /></CardContent></Card>
      <Imprimible titulo={t('flujo.titulo_imp')}
        subtitulo={cmp && comp.data ? t('comun.comparado_con', { base: t('comun.del_al', { desde, hasta }), otro: rotulo(comp.data.anterior) }) : t('comun.del_al', { desde, hasta })}>
        <Card><CardContent className="overflow-x-auto pt-4">
          {cmp ? <TablaComparativa data={comp.data} cargando={comp.isFetching} /> : <>
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b"><td className="py-1.5 px-3 font-medium" colSpan={2}>{t('flujo.saldo_inicial')}</td><td className="py-1.5 px-3 text-right font-medium">{usd(data?.saldo_inicial)}</td></tr>
              <Seccion titulo={t('flujo.entradas')} items={data?.entradas} total={Number(data?.total_entradas || 0)} />
              <Seccion titulo={t('flujo.salidas')} items={data?.salidas} total={Number(data?.total_salidas || 0)} />
              <tr className="border-t-2 font-bold"><td className="py-2 px-3" colSpan={2}>{t('flujo.saldo_final')}</td><td className="py-2 px-3 text-right">{usd(data?.saldo_final)}</td></tr>
            </tbody>
          </table></>}
        </CardContent></Card>
      </Imprimible>
    </div>
  );
}

// ---- Estado de Cambios en el Patrimonio ------------------------------------
export function CambiosPatrimonio() {
  const [desde, setDesde] = useState(inicioMes); const [hasta, setHasta] = useState(hoy);
  const { data, isFetching } = useQuery<any>({ queryKey: ['cont-ecp', desde, hasta], queryFn: () => rpc<any>('cont_cambios_patrimonio', { _desde: desde, _hasta: hasta }) });
  const cuentas = data?.cuentas || [];
  const exportar = () => exportarCSV(`cambios-patrimonio-${desde}_${hasta}`, ['Código', 'Cuenta', 'Saldo inicial', 'Aumentos', 'Disminuciones', 'Saldo final'],
    cuentas.map((r: any) => [r.codigo, r.nombre, r.saldo_inicial, r.aumentos, r.disminuciones, r.saldo_final]));
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-2"><h1 className="text-xl font-semibold">Estado de Cambios en el Patrimonio</h1><div className="flex gap-2"><CsvBtn onClick={exportar} /><PrintButton /></div></div>
      <p className="text-sm text-muted-foreground no-print">Este estado ya muestra el movimiento completo (saldo inicial → saldo final), por lo que no lleva columna comparativa: su comparativo NIIF es un segundo bloque de filas, que se obtiene corriéndolo con el rango del ejercicio anterior.</p>
      <Card><CardContent className="flex flex-wrap items-end gap-3 pt-4"><Fecha label="Desde" value={desde} onChange={setDesde} /><Fecha label="Hasta" value={hasta} onChange={setHasta} /></CardContent></Card>
      <Imprimible titulo="Estado de Cambios en el Patrimonio" subtitulo={`Del ${desde} al ${hasta}`}>
        <Card><CardContent className="overflow-x-auto pt-4">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Cuenta</th><th className="py-2 pr-3 text-right">Saldo inicial</th><th className="py-2 pr-3 text-right">Aumentos</th><th className="py-2 pr-3 text-right">Disminuciones</th><th className="py-2 pr-3 text-right">Saldo final</th></tr></thead>
            <tbody>
              {cuentas.length === 0 && !isFetching && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin cuentas patrimoniales con movimiento</td></tr>}
              {cuentas.map((r: any, i: number) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-1.5 pr-3 font-mono text-xs">{r.codigo}</td><td className="py-1.5 pr-3">{r.nombre}</td>
                  <td className="py-1.5 pr-3 text-right">{usd(r.saldo_inicial)}</td><td className="py-1.5 pr-3 text-right">{usd(r.aumentos)}</td>
                  <td className="py-1.5 pr-3 text-right">{usd(r.disminuciones)}</td><td className="py-1.5 pr-3 text-right font-medium">{usd(r.saldo_final)}</td>
                </tr>
              ))}
              {cuentas.length > 0 && <tr className="border-t font-medium"><td colSpan={2} className="py-2 pr-3">Subtotal patrimonio</td>
                <td className="py-2 pr-3 text-right">{usd(data?.total_inicial)}</td><td className="py-2 pr-3 text-right">{usd(data?.total_aumentos)}</td>
                <td className="py-2 pr-3 text-right">{usd(data?.total_disminuciones)}</td><td className="py-2 pr-3 text-right">{usd(data?.total_final)}</td></tr>}
              <tr className="border-t"><td colSpan={5} className="py-1.5 px-3">Resultado del período (utilidad/pérdida)</td><td className={`py-1.5 pr-3 text-right ${Number(data?.resultado_periodo || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{usd(data?.resultado_periodo)}</td></tr>
              <tr className="border-t-2 font-bold"><td colSpan={5} className="py-2 px-3">PATRIMONIO TOTAL</td><td className="py-2 pr-3 text-right">{usd(data?.patrimonio_total)}</td></tr>
            </tbody>
          </table>
        </CardContent></Card>
      </Imprimible>
    </div>
  );
}

// ---- Mayor Auxiliar por Tercero -------------------------------------------
interface TerceroRow {
  partner_id: string; partner_nombre: string; identificacion: string | null;
  cuenta_id: string; cuenta_codigo: string; cuenta_nombre: string;
  saldo_inicial: number; debe: number; haber: number; saldo_final: number;
  // Las líneas SIN tercero ya no desaparecen: se agrupan en una fila propia
  // para que el auxiliar sume lo mismo que su cuenta de control (script 781).
  sin_tercero?: boolean;
}
interface CuadreRow {
  cuenta_id: string; codigo: string; nombre: string;
  mayor: number; con_tercero: number; sin_tercero: number;
  lineas_sin_tercero: number; diferencia: number;
}
export function MayorTerceros() {
  const [tipo, setTipo] = useState('todos');
  const [desde, setDesde] = useState(inicioMes);
  const [hasta, setHasta] = useState(hoy);
  const [sel, setSel] = useState<TerceroRow | null>(null);
  const { data: filas = [], isFetching } = useQuery<TerceroRow[]>({
    queryKey: ['cont-mayor-terceros', tipo, desde, hasta],
    queryFn: () => rpc<TerceroRow[]>('cont_mayor_terceros', { _desde: desde, _hasta: hasta, _tipo: tipo }),
  });
  // El cuadre contra la cuenta de control. Un auxiliar que no cuadra y no avisa
  // es peor que no tener auxiliar: se entrega como papel de trabajo y se firma.
  const { data: cuadre = [] } = useQuery<CuadreRow[]>({
    queryKey: ['cont-mayor-terceros-cuadre', tipo, desde, hasta],
    queryFn: () => rpc<CuadreRow[]>('cont_mayor_terceros_cuadre', { _desde: desde, _hasta: hasta, _tipo: tipo }),
  });
  const { data: det, isFetching: detCargando } = useQuery<any>({
    queryKey: ['cont-mayor-tercero-det', sel?.partner_id, desde, hasta], enabled: !!sel,
    queryFn: () => rpc<any>('cont_mayor_tercero_detalle', { _partner_id: sel!.partner_id, _desde: desde, _hasta: hasta }),
  });
  let saldo = Number(det?.saldo_inicial || 0);
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-2"><h1 className="text-xl font-semibold">Mayor Auxiliar por Tercero</h1>
        <div className="flex gap-2"><CsvBtn onClick={() => exportarCSV('mayor-terceros', ['Tercero', 'Identificación', 'Cuenta', 'Saldo inicial', 'Debe', 'Haber', 'Saldo final'], filas.map((r) => [r.partner_nombre, r.identificacion, `${r.cuenta_codigo} ${r.cuenta_nombre}`, r.saldo_inicial, r.debe, r.haber, r.saldo_final]))} /><PrintButton /></div>
      </div>
      <Card><CardContent className="flex flex-wrap items-end gap-3 pt-4">
        <label className="flex flex-col text-xs text-muted-foreground">Tipo
          <select className={`${field} w-44 mt-1`} value={tipo} onChange={(e) => { setTipo(e.target.value); setSel(null); }}>
            <option value="todos">Todos</option><option value="cliente">Clientes</option><option value="proveedor">Proveedores</option>
          </select></label>
        <Fecha label="Desde" value={desde} onChange={(v) => { setDesde(v); setSel(null); }} /><Fecha label="Hasta" value={hasta} onChange={(v) => { setHasta(v); setSel(null); }} />
      </CardContent></Card>
      <Imprimible titulo="Mayor Auxiliar por Tercero" subtitulo={`Del ${desde} al ${hasta}`}>
        <Card><CardHeader className="pb-2"><CardTitle className="text-base">Saldos por tercero</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Tercero</th><th className="py-2 pr-3">Identificación</th><th className="py-2 pr-3">Cuenta</th><th className="py-2 pr-3 text-right">Saldo inicial</th><th className="py-2 pr-3 text-right">Debe</th><th className="py-2 pr-3 text-right">Haber</th><th className="py-2 pr-3 text-right">Saldo final</th></tr></thead>
              <tbody>
                {filas.length === 0 && !isFetching && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Sin movimientos por tercero en el rango</td></tr>}
                {filas.map((r, i) => (
                  <tr key={i} className={`border-b last:border-0 hover:bg-muted/50 ${r.sin_tercero ? 'bg-amber-500/5' : 'cursor-pointer'}`}
                    onClick={() => { if (!r.sin_tercero) setSel(r); }}>
                    <td className={`py-1.5 pr-3 ${r.sin_tercero ? 'italic text-amber-700 dark:text-amber-500' : ''}`}
                      title={r.sin_tercero ? 'Líneas de esta cuenta que no llevan tercero. Antes desaparecían del auxiliar y por eso no cuadraba con la cuenta de control.' : undefined}>
                      {r.partner_nombre}</td><td className="py-1.5 pr-3 font-mono text-xs">{r.identificacion || ''}</td>
                    <td className="py-1.5 pr-3 text-xs">{r.cuenta_codigo} · {r.cuenta_nombre}</td>
                    <td className="py-1.5 pr-3 text-right">{usd(r.saldo_inicial)}</td><td className="py-1.5 pr-3 text-right">{usd(r.debe)}</td>
                    <td className="py-1.5 pr-3 text-right">{usd(r.haber)}</td><td className="py-1.5 pr-3 text-right font-medium">{usd(r.saldo_final)}</td>
                  </tr>
                ))}
              </tbody>
              {/* El cuadre contra la cuenta de control, al pie. Antes esta
                  pantalla sumaba 190.531,11 contra un mayor de 88.010,92 y no
                  lo decía en ninguna parte. */}
              {cuadre.length > 0 && (
                <tfoot className="border-t-2">
                  {cuadre.map((c) => {
                    const aux = Number(c.con_tercero || 0) + Number(c.sin_tercero || 0);
                    const desc = Math.abs(Number(c.diferencia || 0)) > 0.005;
                    return (
                      <tr key={c.cuenta_id} className={desc ? 'bg-red-500/5' : 'bg-muted/40'}>
                        <td className="py-2 pr-3 text-xs" colSpan={3}>
                          <b>{c.codigo} {c.nombre}</b>
                          {Number(c.lineas_sin_tercero) > 0 && (
                            <span className="ml-2 text-amber-600">
                              {c.lineas_sin_tercero} línea(s) sin tercero
                            </span>)}
                        </td>
                        <td className="py-2 pr-3 text-right text-xs text-muted-foreground">Auxiliar</td>
                        <td className="py-2 pr-3 text-right text-xs tabular-nums">{usd(aux)}</td>
                        <td className="py-2 pr-3 text-right text-xs text-muted-foreground">Mayor</td>
                        <td className={`py-2 pr-3 text-right text-xs tabular-nums font-semibold ${desc ? 'text-red-600' : ''}`}
                          title={desc ? `Diferencia de ${usd(c.diferencia)}` : 'El auxiliar cuadra con su cuenta de control'}>
                          {usd(c.mayor)}{desc ? ` (△ ${usd(c.diferencia)})` : ' ✓'}
                        </td>
                      </tr>
                    );
                  })}
                </tfoot>
              )}
            </table>
          </CardContent></Card>
      </Imprimible>
      {sel && (
        <Imprimible titulo={`Mayor de ${sel.partner_nombre}`} subtitulo={`${sel.cuenta_codigo} ${sel.cuenta_nombre} · Del ${desde} al ${hasta}`}>
          <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-base">Detalle · {sel.partner_nombre}</CardTitle>
            <div className="flex gap-2 no-print"><CsvBtn onClick={() => exportarCSV(`mayor-${sel.identificacion || 'tercero'}`, ['Fecha', 'N°', 'Glosa', 'Cuenta', 'Debe', 'Haber'], (det?.movimientos || []).map((m: any) => [m.fecha, m.numero, m.glosa, `${m.cuenta_codigo} ${m.cuenta_nombre}`, m.debe, m.haber]))} /><PrintButton /></div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {detCargando && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">N°</th><th className="py-2 pr-3">Glosa</th><th className="py-2 pr-3">Cuenta</th><th className="py-2 pr-3 text-right">Debe</th><th className="py-2 pr-3 text-right">Haber</th><th className="py-2 pr-3 text-right">Saldo</th></tr></thead>
              <tbody>
                <tr className="border-b text-muted-foreground"><td className="py-2 pr-3" colSpan={6}>Saldo inicial</td><td className="py-2 pr-3 text-right">{usd(saldo)}</td></tr>
                {(det?.movimientos || []).map((m: any, i: number) => { saldo += Number(m.debe) - Number(m.haber); return (
                  <tr key={i} className="border-b last:border-0"><td className="py-1.5 pr-3 whitespace-nowrap">{m.fecha}</td><td className="py-1.5 pr-3 font-mono text-xs">{m.numero}</td>
                    <td className="py-1.5 pr-3">{m.glosa}</td><td className="py-1.5 pr-3 text-xs">{m.cuenta_codigo}</td>
                    <td className="py-1.5 pr-3 text-right">{Number(m.debe) ? usd(m.debe) : ''}</td><td className="py-1.5 pr-3 text-right">{Number(m.haber) ? usd(m.haber) : ''}</td>
                    <td className="py-1.5 pr-3 text-right">{usd(saldo)}</td></tr>); })}
                <tr className="border-t font-medium"><td className="py-2 pr-3" colSpan={4}>Totales</td><td className="py-2 pr-3 text-right">{usd(det?.total_debe)}</td><td className="py-2 pr-3 text-right">{usd(det?.total_haber)}</td><td className="py-2 pr-3 text-right">{usd(saldo)}</td></tr>
              </tbody>
            </table>
          </CardContent></Card>
        </Imprimible>
      )}
    </div>
  );
}
